import { DEFECT_STATUS } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { useMyDefects } from '../../lib/my-defects.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { useUrlState } from '../../lib/use-url-state.js';
import { MyDefectPanel } from './my-defect-panel.js';
import { MyDefectRow } from './my-defects.js';

// #192: „Meine Fehler" — alle Defects des Testers an einer Stelle (aus eigenen Läufen und selbst
// angelegt), mit Statusfilter. DEF-7: Tester sehen das Board nicht; dies ist ihre Lesesicht. Die
// Liste unter der Ausführung zeigt nur den gewählten Testfall, hier steht das Ganze — deshalb mit
// Testfall in jeder Zeile. Details und die Notiz liegen in derselben Seitenleiste wie dort.
export function MyDefectsPage() {
  const { t } = useTranslation();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id ?? '';
  const { data, isLoading, isError, refetch } = useMyDefects(projectId);
  // Der Filter lebt in der URL (deep-linkbar, reload-fest). Ein manipulierter Wert fällt auf „Alle"
  // zurück, statt eine Liste zu zeigen, die auf keinen Status passt.
  const [statusParam, setStatus] = useUrlState('status', '');
  const status = (DEFECT_STATUS as readonly string[]).includes(statusParam) ? statusParam : '';
  const [openDefectId, setOpenDefectId] = useState<string | null>(null);

  const rows = status ? data.filter((d) => d.status === status) : data;

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-bold text-text-primary">{t('myDefects.title')}</h1>
        {/* Ohne Defects gibt es nichts zu filtern; und bei einem Ladefehler wäre der Filter
            über einer Liste, die es nicht gibt, irreführend. */}
        {!isError && data.length > 0 && (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-text-muted">{t('myDefects.filterStatus')}</span>
            <select
              className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">{t('myDefects.allStatuses')}</option>
              {DEFECT_STATUS.map((s) => (
                <option key={s} value={s}>
                  {t(`defect.status.${s}`)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {projectLoading || isLoading ? (
        <div className="text-text-muted">{t('common.loading')}</div>
      ) : isError ? (
        // #89: ein Ladefehler darf nie als „Sie haben noch keine Fehler gemeldet" erscheinen.
        <QueryErrorNotice onRetry={refetch} />
      ) : data.length === 0 ? (
        <div className="text-sm text-text-muted">{t('myDefects.empty')}</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-text-muted">{t('myDefects.noneForFilter')}</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((d) => (
            <li key={d.defect_id}>
              <MyDefectRow defect={d} onOpen={setOpenDefectId} showTestCase />
            </li>
          ))}
        </ul>
      )}

      {openDefectId && projectId && (
        <MyDefectPanel
          projectId={projectId}
          defectId={openDefectId}
          onClose={() => setOpenDefectId(null)}
        />
      )}
    </div>
  );
}
